import usePushNotifications from "@/hooks/usePushNotifications";
import KipaIcon from "@/components/kipa/KipaIcon";
import { Bell } from "lucide-react";

/**
 * Bottom-sheet prompt to enable web push notifications.
 * Auto-shown once after onboarding; dismissible — won't re-prompt.
 */
export default function PushPromptSheet({ open, onClose }) {
  const { supported, subscribe, busy } = usePushNotifications();

  if (!open) return null;

  const onEnable = async () => {
    await subscribe();
    onClose();
  };

  const onLater = () => {
    localStorage.setItem("bizpa_push_prompted", "1");
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      data-testid="push-prompt-sheet"
      onClick={onLater}
    >
      <div
        className="w-full max-w-[430px] bg-white rounded-t-[28px] p-6 pb-8"
        style={{ animation: "pushSheetIn 260ms ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-2xl bg-[var(--al)] flex items-center justify-center">
            <KipaIcon icon={Bell} size="lg" color="var(--color-primary)" />
          </div>
        </div>
        <div className="text-[20px] font-black text-[var(--tx)] text-center mb-2 leading-tight">
          Never miss a money update
        </div>
        <div className="text-[13px] text-[var(--tx2)] text-center mb-5 leading-relaxed">
          Get daily summaries of sales, unpaid invoices, and budget alerts delivered straight
          to your phone — even when KipaBiz is closed.
        </div>

        {!supported && (
          <div className="bg-[var(--rl)] border border-[var(--rdim)] rounded-xl p-3 mb-4 text-[12px] text-[var(--rd)]" data-testid="push-unsupported">
            Your browser doesn't support web push. You can still receive in-app alerts.
          </div>
        )}

        <button
          onClick={onEnable}
          disabled={!supported || busy}
          data-testid="push-prompt-enable"
          className="w-full bg-[var(--ac)] text-white rounded-2xl py-3.5 font-extrabold text-[14px] disabled:opacity-50 mb-2 inline-flex items-center justify-center gap-2"
          style={supported ? { boxShadow: "0 4px 20px rgba(13,110,110,0.28)" } : {}}
        >
          {busy ? "Enabling…" : (
            <>
              <KipaIcon icon={Bell} size="sm" color="#fff" />
              Enable Notifications
            </>
          )}
        </button>
        <button
          onClick={onLater}
          data-testid="push-prompt-later"
          className="w-full bg-transparent text-[var(--tx2)] rounded-2xl py-2.5 font-semibold text-[13px]"
        >
          Not now
        </button>
      </div>
      <style>{`
        @keyframes pushSheetIn {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
